/**
 * DocumentDetailPage
 * 
 * Full page view for a single document with metadata and version history
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Download, Share2, Pencil, Trash2, 
  FileText, Calendar, User, Folder, Tag, Clock,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/core/lib/utils';

import { useDocument, useDeleteDocument, useDocumentDownloadUrl } from '../hooks/useDocuments';
import { Document, DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_COLORS } from '../types/documentsTypes';
import { DocumentViewer } from '../components/DocumentViewer';
import { DocumentVersionHistory } from '../components/DocumentVersionHistory';
import { ShareWithAttorneyModal } from '@/features/attorney/components/ShareWithAttorneyModal';

// ============================================================================
// Helpers
// ============================================================================

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// ============================================================================
// Component
// ============================================================================

export const DocumentDetailPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  
  const [showViewer, setShowViewer] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const { data: document, isLoading, error } = useDocument(documentId!);
  const deleteMutation = useDeleteDocument();
  const { refetch: getDownloadUrl } = useDocumentDownloadUrl(documentId!, { enabled: false });

  const handleDownload = async () => {
    const { data } = await getDownloadUrl();
    if (data?.url) {
      window.open(data.url, '_blank');
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Delete "${document?.title}"? This cannot be undone.`)) {
      await deleteMutation.mutateAsync(documentId!);
      navigate('/family/documents');
    }
  };

  const handleShare = async (documentIds: string[], notes?: string) => {
    // API call to share
    console.log('Sharing:', documentIds, notes);
    setShowShareModal(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream p-6">
        <div className="max-w-4xl mx-auto">
          <div className="h-8 w-32 bg-gray-200 rounded animate-pulse mb-6" />
          <div className="bg-white rounded-xl p-6">
            <div className="h-64 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="min-h-screen bg-cream p-6 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 mx-auto text-warm-gray mb-4" />
          <h2 className="text-xl font-semibold text-navy mb-2">Document Not Found</h2>
          <p className="text-warm-gray mb-4">The document you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/family/documents')}
            className="text-sage hover:text-sage-dark"
          >
            Return to Document Vault
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-warm-gray hover:text-navy transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-cream transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Download</span>
              </button>
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-cream transition-colors"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
              <button
                onClick={() => navigate(`/family/documents/${documentId}/edit`)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-cream transition-colors"
              >
                <Pencil className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Preview Card */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <button
                onClick={() => setShowViewer(true)}
                className="w-full h-64 bg-cream flex items-center justify-center hover:bg-cream-dark transition-colors group"
              >
                <div className="text-center">
                  <FileText className="w-16 h-16 mx-auto text-warm-gray mb-3 group-hover:text-sage transition-colors" />
                  <span className="text-sage font-medium flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Open Preview
                  </span>
                </div>
              </button>
              
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-semibold text-navy mb-2">
                      {document.title}
                    </h1>
                    <span className={cn(
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium',
                      DOCUMENT_TYPE_COLORS[document.document_type]
                    )}>
                      {DOCUMENT_TYPE_LABELS[document.document_type]}
                    </span>
                  </div>
                </div>

                {document.description && (
                  <p className="mt-4 text-warm-gray">{document.description}</p>
                )}
              </div>
            </div>

            {/* Version History */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <DocumentVersionHistory
                versions={[]} // Would come from API
                currentVersion={document.version}
                onDownload={(v) => console.log('Download version:', v)}
                onRestore={(v) => console.log('Restore version:', v)}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="font-semibold text-navy mb-4">Details</h2>
              
              <dl className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-warm-gray mt-0.5" />
                  <div>
                    <dt className="text-sm text-warm-gray">Document Date</dt>
                    <dd className="text-navy">
                      {document.document_date 
                        ? format(new Date(document.document_date), 'MMMM d, yyyy')
                        : 'Not specified'}
                    </dd>
                  </div>
                </div>

                {document.provider_name && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-warm-gray mt-0.5" />
                    <div>
                      <dt className="text-sm text-warm-gray">Provider</dt>
                      <dd className="text-navy">{document.provider_name}</dd>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Folder className="w-5 h-5 text-warm-gray mt-0.5" />
                  <div>
                    <dt className="text-sm text-warm-gray">File Size</dt>
                    <dd className="text-navy">{formatFileSize(document.file_size_bytes)}</dd>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-warm-gray mt-0.5" />
                  <div>
                    <dt className="text-sm text-warm-gray">Uploaded</dt>
                    <dd className="text-navy">
                      {format(new Date(document.created_at), 'MMM d, yyyy \'at\' h:mm a')}
                    </dd>
                  </div>
                </div>

                {document.tags && document.tags.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Tag className="w-5 h-5 text-warm-gray mt-0.5" />
                    <div>
                      <dt className="text-sm text-warm-gray">Tags</dt>
                      <dd className="flex flex-wrap gap-1 mt-1">
                        {document.tags.map(tag => (
                          <span 
                            key={tag}
                            className="px-2 py-0.5 bg-cream rounded text-sm text-navy"
                          >
                            {tag}
                          </span>
                        ))}
                      </dd>
                    </div>
                  </div>
                )}
              </dl>
            </div>

            {/* Sharing Status */}
            {document.is_shared_with_attorney && (
              <div className="bg-sage/5 border border-sage/20 rounded-xl p-6">
                <div className="flex items-center gap-2 text-sage mb-2">
                  <Share2 className="w-5 h-5" />
                  <h3 className="font-medium">Shared with Attorney</h3>
                </div>
                <p className="text-sm text-warm-gray">
                  This document is accessible to attorneys on your legal case.
                </p>
                {document.shared_at && (
                  <p className="text-xs text-warm-gray mt-2">
                    Shared on {format(new Date(document.shared_at), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      {showViewer && (
        <DocumentViewer
          document={document}
          onClose={() => setShowViewer(false)}
          onAction={(action) => {
            if (action === 'download') handleDownload();
            if (action === 'share') setShowShareModal(true);
            if (action === 'delete') handleDelete();
          }}
        />
      )}

      <ShareWithAttorneyModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        onRevoke={() => {}}
        documents={[document]}
      />
    </div>
  );
};

export default DocumentDetailPage;


